/**
 * Map glyph set — authored on a 20x20 grid per the design handoff, rendered
 * with currentColor so one path set serves chips (18px), the settings sheet
 * (20px), and map tiles (20px inside a 26px tile, 3px inset).
 *
 * Each entry is the inner SVG markup for a viewBox="0 0 20 20" element.
 * Stroke-based unless noted; stroke width rides in the markup because the
 * handoff varies it per glyph (1.7 / 1.8 / 2 / 2.2).
 */

const S = (d, w = 1.7) =>
  `<path d="${d}" fill="none" stroke="currentColor" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`;
const F = (d) => `<path d="${d}" fill="currentColor"/>`;
const C = (cx, cy, r, w = 1.7) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="currentColor" stroke-width="${w}"/>`;
const DOT = (cx, cy, r) => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="currentColor"/>`;

export const MAP_ICONS = {
  medical:
    S('M6.6 10h6.8 M10 6.6v6.8', 2) +
    `<rect x="3.2" y="3.2" width="13.6" height="13.6" rx="3.6" fill="none" stroke="currentColor" stroke-width="1.7"/>`,
  water: F('M10 3l4.4 6.7a5.3 5.3 0 1 1-8.8 0z'),
  restroom:
    DOT(6.4, 4.2, 1.8) +
    S('M6.4 7.2v8.6 M3.8 9.8h5.2', 1.8) +
    DOT(13.8, 4.2, 1.8) +
    S('M13.8 7.2v8.6 M11.4 12.2l2.4-5 2.4 5', 1.8),
  food:
    S('M6.6 3.4v13.2 M4.6 3.4v3.6a2 2 0 0 0 4 0V3.4') +
    S('M13.4 16.6V9.8a2.6 2.6 0 0 1 0-5.2 2.6 2.6 0 0 1 0 5.2'),
  bar: S('M3.4 4.6h13.2L10 11.4z M10 11.4v4.4 M6.8 16.4h6.4'),
  lockers:
    `<rect x="4.4" y="8.4" width="11.2" height="7.6" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.7"/>` +
    S('M7.2 8.4V6.6a2.8 2.8 0 0 1 5.6 0v1.8'),
  charging: F('M11 3l-5 8h3.4l-.8 6 5.4-8.6H10.4z'),
  info: C(10, 10, 7) + S('M10 8.8v5') + DOT(10, 6.2, 1.1),
  ice: S('M10 3v14 M4 6.6l12 6.8 M16 6.6L4 13.4'),
  bike: C(5.4, 13.4, 3.2) + C(14.6, 13.4, 3.2) + S('M5.4 13.4l3.4-6h3.4l2.4 6'),
  shade: S('M3 10a7 7 0 0 1 14 0z M10 10v6'),
  cash:
    C(10, 10, 7) +
    S('M12 7.6a2.4 2.4 0 0 0-4.4 1.2c0 2.2 4.4 1 4.4 3.2A2.4 2.4 0 0 1 8 12.6 M10 5.4v9.2', 1.4),
  merch: S('M7.4 3.6L4 5.8l1.4 3 1.6-.8V16h6V8l1.6.8 1.4-3-3.4-2.2a2.6 2.6 0 0 1-5.2 0z'),
  box_office:
    S('M3 7.4a2 2 0 0 0 0 5.2V15h14v-2.4a2 2 0 0 1 0-5.2V5H3z') +
    `<path d="M10 7.6v4.8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-dasharray="1.6 1.8" stroke-linecap="round"/>`,
  general_store: S('M3 7.6h14l-1.6 8.8H4.6z M7 7.6l1.6-4 M13 7.6l-1.6-4'),
  id_check:
    `<rect x="3.4" y="5" width="13.2" height="10" rx="1.8" fill="none" stroke="currentColor" stroke-width="1.7"/>` +
    DOT(7.6, 9, 1.8) +
    S('M11.4 8.2h2.8 M11.4 11h2.8', 1.4),
  wifi: S('M3.6 8.2a9.4 9.4 0 0 1 12.8 0 M6.2 11.2a5.8 5.8 0 0 1 7.6 0') + DOT(10, 14.6, 1.5),
  shower: S(
    'M4 4h6a4 4 0 0 1 4 4 M4.4 9.4h11.2 M6.4 12v.8 M9.4 12.4v.8 M12.4 12v.8 M7.9 14.6v.8 M11 14.8v.8',
    1.5,
  ),
  ada: DOT(9.4, 4, 1.8) + S('M9.4 7v4.4h4 M13.4 11.4l1.4 4.6 M6 9.2a5 5 0 1 0 6.4 6.8', 1.8),
  // Check-in is the same figure over a counter — the desk is what separates
  // a place you report to from a place you watch from, and it survives at
  // 18px where a badge or a second mark inside the wheel does not.
  ada_checkin:
    DOT(8.6, 3.4, 1.5) +
    S('M8.6 5.9v3.7h3.4 M11.9 9.6l1.2 3.9 M5.6 7.6a4.2 4.2 0 1 0 5.4 5.7', 1.6) +
    S('M2.8 17h14.4', 1.8),
  rideshare:
    S(
      'M2.6 12.6h14.8v-2a1.6 1.6 0 0 0-1.2-1.5l-1.8-.4-2-2.6a1.8 1.8 0 0 0-1.4-.7H7.4a1.8 1.8 0 0 0-1.5.8L4.4 8.6l-1.2.4a1.4 1.4 0 0 0-1 1.4z',
      1.5,
    ) +
    C(5.6, 14.4, 1.8, 1.5) +
    C(14.2, 14.4, 1.8, 1.5),
  parking: S('M6.4 16.4V4h4.2a3.6 3.6 0 0 1 0 7.2H6.4', 2.2),
  // A bus, front-on: body, windscreen, two headlights. The Shuttle chip split
  // off Rides on 2026-09-17 — before that a shuttle was a rideshare pin with
  // "shuttle" in its name, and one chip toggled both lots.
  shuttle:
    `<rect x="3.6" y="3.4" width="12.8" height="11.2" rx="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/>` +
    S('M3.6 9.4h12.8 M6.4 14.6v1.8 M13.6 14.6v1.8', 1.7) +
    DOT(6.6, 12.2, 1.1) +
    DOT(13.4, 12.2, 1.1),
  // Zone pins: a star for VIP / premium / hospitality, a tent for camping.
  vip: S('M10 3.2l2.1 4.4 4.8.6-3.5 3.3.9 4.8L10 14l-4.3 2.3.9-4.8L3.1 8.2l4.8-.6z', 1.6),
  camping: S('M10 3.6L3 16.4h14z M10 3.6v12.8 M7.2 16.4l2.8-5.2 2.8 5.2', 1.6),
  // A stage: a platform with a truss arch over it.
  stage:
    S('M3 16.4h14 M4.6 16.4v-4.2h10.8v4.2', 1.7) + S('M4.6 12.2V8.6a5.4 5.4 0 0 1 10.8 0v3.6', 1.7),
  entrance: S('M11.6 3.4h4v13.2h-4 M3.4 10h7 M7.6 6.6l3.4 3.4-3.4 3.4', 1.8),
  exit: S('M8.4 3.4h-4v13.2h4 M9.6 10h7 M13 6.6l3.4 3.4-3.4 3.4', 1.8),
  // An activation: a pop-up booth under a scalloped awning - a brand's stall, a sponsor's lounge, The Cabin.
  // Sam's pick (2026-10-06, option D in gold) over the grey diamond, which read as lifeless on a building's roof.
  activation: S(
    'M3.4 7.6l1.4-3.6h10.4l1.4 3.6z M3.4 7.6a2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4 0 2.2 2.2 0 0 0 4.4 0 M4.6 10v6.4h10.8V10',
  ),
  // Index tail + anything unmapped: one neutral diamond covers all of it.
  other: S('M10 3.6l6.4 6.4L10 16.4 3.6 10z', 1.7),
  // An Other amenity (cushion rental, a one-off service no chip names): the same diamond, but its own glyph key, so the
  // index tail's muting (glyph 'other') leaves it in full colour on its building (Sam, 2026-10-22).
  misc: S('M10 3.6l6.4 6.4L10 16.4 3.6 10z', 1.7),
};

// Kinds without their own glyph borrow the closest one.
const FALLBACK = {
  art: 'other',
  ride: 'other',
  ga_area: 'other',
  towel_zone: 'other',
  standing_area: 'other',
  safety_phone: 'medical',
};

export function iconMarkup(kind, size = 20) {
  const inner = MAP_ICONS[kind] ?? MAP_ICONS[FALLBACK[kind] ?? 'other'] ?? MAP_ICONS.other;
  return `<svg width="${size}" height="${size}" viewBox="0 0 20 20" aria-hidden="true">${inner}</svg>`;
}
